import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePopover } from '@/components/ui/date-popover';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import {
    MapPin,
    Sparkles,
    Book,
    Users,
    Church,
    User
} from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { PersonProfileModal } from './person-profile-modal';

interface PersonCardProps {
    personInPassage: PersonInPassage;
    person?: Person | null;
    bookName?: string;
    isExpanded?: boolean;
    onToggleExpansion?: () => void;
}


/**
 * Shared component for displaying a person in a passage.
 * Shows passage-specific context (role, relevance) and optionally expands to show full person details.
 */
export function PersonCard({
    personInPassage,
    person,
    bookName,
    isExpanded = false,
    onToggleExpansion
}: PersonCardProps) {
    const isDarkMode = useDarkMode();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const hasDetails = !!(
        person && (
            person.summary ||
            person.lifespan ||
            (person.itinerary && person.itinerary.length > 0) ||
            (person.biblicalReferences && person.biblicalReferences.length > 0) ||
            (person.relationships && person.relationships.length > 0) ||
            person.description ||
            (person.alternateNames && person.alternateNames.length > 0) ||
            person.geography ||
            person.biblicalRelevance ||
            person.biography ||
            person.ministry ||
            person.theology ||
            person.character ||
            person.tradition ||
            person.historicalContext
        )
    );

    return (
        <>
            <DiscoveryCard
                isExpanded={false}
                onToggle={() => { }}
                canExpand={false}
                children={null}
                icon={User}
                header={
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                            {/* Source Header */}
                            {personInPassage.verseRef && bookName && (
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40 flex items-center gap-1.5 mb-1">
                                    {bookName} {personInPassage.verseRef}
                                </div>
                            )}

                            <div className="flex items-start justify-between gap-4">
                                <div className="space-y-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {/* Person Name */}
                                        <h4 className="text-xl font-serif font-medium text-foreground/90 tracking-tight leading-tight">
                                            {personInPassage.displayNameInPassage || personInPassage.personName}
                                        </h4>

                                        {hasDetails && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsModalOpen(true);
                                                }}
                                                className="rounded-full bg-primary/10 border-primary/20 text-primary text-[9px] uppercase font-bold tracking-[0.1em] h-6 px-3 hover:bg-primary/20 hover:border-primary/40 transition-all shrink-0 flex items-center gap-1.5"
                                            >
                                                <span>Deep Dive</span>
                                                <Sparkles className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="text-xs font-bold uppercase tracking-widest text-primary/60">
                                        {personInPassage.roleInPassage}
                                    </div>
                                </div>
                            </div>

                            {/* Passage Relevance */}
                            {personInPassage.passageRelevance && (
                                <p className="text-sm text-foreground/70 font-light leading-relaxed mt-2 italic font-serif">
                                    {personInPassage.passageRelevance}
                                </p>
                            )}
                        </div>
                    </div>
                }
            />

            {person && (
                <PersonProfileModal
                    isOpen={isModalOpen}
                    onOpenChange={setIsModalOpen}
                    person={person}
                    personInPassage={personInPassage}
                    bookName={bookName}
                />
            )}
        </>
    );
}



